import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  formatarMoedaBRL,
  formatarNumero,
  formatarPercentual,
} from "../helpers/financeiro-formatters";

export type FinanceiroResumoCardProps = {
  titulo: string;
  valor: string | number | null | undefined;
  descricao?: string;
  loading?: boolean;
  tipo?: "positivo" | "negativo" | "neutro" | "alerta";
  prefixo?: string;
  sufixo?: string;
  formato?: "moeda" | "numero" | "percentual";
  /** Quando true, `valor` já está em escala 0–100 para percentual. */
  percentualJaEscalado?: boolean;
  chip?: ReactNode;
  /** Métrica não disponível neste endpoint — exibe traço. */
  indisponivel?: boolean;
  /** Ícone à esquerda (estilo KPI pedidos). Omitido: ícone padrão. */
  icon?: ReactNode;
  /** Rota interna — torna o card clicável (ex.: detalhe operacional). */
  to?: string;
};

function formatValor(
  valor: string | number | null | undefined,
  formato: FinanceiroResumoCardProps["formato"],
  percentualJaEscalado: boolean | undefined
): string {
  if (valor === null || valor === undefined || valor === "") {
    if (formato === "moeda") return formatarMoedaBRL(0);
    return "—";
  }
  if (formato === "percentual") {
    return formatarPercentual(valor, {
      jaEhPercentual: Boolean(percentualJaEscalado),
    });
  }
  if (formato === "numero") return formatarNumero(valor);
  return formatarMoedaBRL(valor);
}

export default function FinanceiroResumoCard({
  titulo,
  valor,
  descricao,
  loading,
  tipo = "neutro",
  prefixo,
  sufixo,
  formato = "moeda",
  percentualJaEscalado,
  chip,
  indisponivel,
  icon,
  to,
}: FinanceiroResumoCardProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;

  const accentTeal = mode === "dark" ? "#2dd4bf" : "#0d9488";
  const accentTealSoft = alpha(accentTeal, mode === "dark" ? 0.16 : 0.12);

  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);
  const borderIconSoft =
    mode === "dark" ? alpha("#81c784", 0.4) : alpha("#66bb6a", 0.28);

  const valorColor =
    tipo === "positivo"
      ? theme.palette.success.main
      : tipo === "negativo"
        ? theme.palette.error.main
        : tipo === "alerta"
          ? theme.palette.warning.main
          : theme.palette.text.primary;

  const texto = loading
    ? ""
    : indisponivel
      ? "—"
      : formatValor(valor, formato, percentualJaEscalado);

  const iconNode = icon ?? <AssessmentOutlinedIcon sx={{ fontSize: 26 }} />;
  const clicavel = Boolean(to) && !loading;

  return (
    <Card
      elevation={0}
      {...(clicavel ? { component: Link, to } : {})}
      aria-label={clicavel ? `${titulo} — ver detalhes` : undefined}
      sx={{
        height: "100%",
        borderRadius: "20px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${borderSoft}`,
        boxShadow: "0 4px 24px rgba(15, 23, 42, 0.06)",
        overflow: "hidden",
        position: "relative",
        display: "block",
        textDecoration: "none",
        color: "inherit",
        cursor: clicavel ? "pointer" : "default",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
          boxShadow: "0 8px 28px rgba(15, 23, 42, 0.09)",
          ...(clicavel ? { transform: "translateY(-1px)" } : {}),
        },
      }}
    >
      <Box sx={{ p: 2.75 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: accentTealSoft,
              color: accentTeal,
              border: `1px solid ${borderIconSoft}`,
            }}
          >
            {loading ? (
              <Skeleton variant="rounded" width={26} height={26} />
            ) : (
              iconNode
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color="text.primary"
              sx={{ letterSpacing: 0.2, mb: 0.25 }}
            >
              {titulo}
            </Typography>
            <Box
              display="flex"
              alignItems="baseline"
              gap={0.5}
              flexWrap="wrap"
              sx={{ mb: descricao ? 0.75 : 0 }}
            >
              {loading ? (
                <Skeleton variant="text" width={140} height={36} />
              ) : (
                <Typography
                  variant="h5"
                  component="div"
                  fontWeight={700}
                  color={valorColor}
                  sx={{
                    lineHeight: 1.25,
                    fontSize: { xs: "1.35rem", sm: "1.5rem" },
                  }}
                >
                  {prefixo}
                  {texto}
                  {sufixo}
                </Typography>
              )}
              {chip}
            </Box>
            {descricao ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.45, display: "block" }}
              >
                {descricao}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </Box>
    </Card>
  );
}
